import { useCallback, useEffect, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useCreateAssigneeNoteMutation,
  useUpdateAssigneeNoteMutation,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import {
  addChip,
  addRegistryEntry,
  closeNote,
  markSessionSaved,
  minimizeNote,
  type NoteSession,
  removeChip,
  restoreNote,
  selectMinimizedNoteSessions,
  selectOpenNoteSession,
  setDraftBody,
  setSessionNoteId,
  updateChip,
} from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Alert, Box, Snackbar, SxProps, Theme } from '@mui/material';

import { buildNoteSavePayload } from './coachNoteDraft';
import { CoachNoteEditorModal, type SaveStatus } from './CoachNoteEditorModal';
import { MinimizedNote } from './MinimizedNote';

const strings = appStrings.modules.coachingDashboard.coachNotes;

const AUTOSAVE_DEBOUNCE_MS = 800;

const sameDraft = (a: NoteSession, b: NoteSession): boolean =>
  JSON.stringify([a.body, a.chips, a.registry]) === JSON.stringify([b.body, b.chips, b.registry]);

export const CoachNoteEditorHost = () => {
  const dispatch = useAppDispatch();
  // The note editor belongs to the student profile on screen. Sessions for other
  // students stay in state and reappear when you return to that student.
  const memberMatch = useMatch(APP_ROUTE.COACHING_DASHBOARD_MEMBER);
  const viewedSubjectId = memberMatch?.params.memberId ? Number(memberMatch.params.memberId) : null;
  const activeSession = useAppSelector(selectOpenNoteSession);
  const openSession =
    activeSession && activeSession.subjectId === viewedSubjectId ? activeSession : null;
  const minimizedSessions = useAppSelector(selectMinimizedNoteSessions).filter(
    (s) => s.subjectId === viewedSubjectId,
  );
  const sessions = useAppSelector((state) => state.coachNoteEditor.sessions);
  const [createNote] = useCreateAssigneeNoteMutation();
  const [updateNote] = useUpdateAssigneeNoteMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sessionsRef = useRef(sessions);

  sessionsRef.current = sessions;

  // Session ids with a create POST in flight, to avoid double-creating a note.
  const creatingRef = useRef<Set<string>>(new Set());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The session a pending debounced save belongs to. The save resolves the
  // session from the latest store state at fire time, so switching notes
  // mid-debounce can't misdirect it to the wrong note.
  const pendingSaveIdRef = useRef<string | null>(null);
  const saveRef = useRef<(session: NoteSession) => Promise<void>>();

  const save = useCallback(
    async (session: NoteSession) => {
      const payload = buildNoteSavePayload(session.body, session.chips, session.registry);

      if (!payload) {
        setSaving(false);

        return;
      }

      try {
        if (session.noteId == null) {
          if (creatingRef.current.has(session.id)) return;

          creatingRef.current.add(session.id);

          let created;

          try {
            created = await createNote({ userId: session.subjectId, ...payload }).unwrap();

            dispatch(
              setSessionNoteId({
                id: session.id,
                noteId: created.id,
                createdAt: created.created_at,
                lastSavedAt: created.updated_at,
                author: created.author,
              }),
            );
          } finally {
            creatingRef.current.delete(session.id);
          }

          // Persist anything typed while the create POST was in flight — that
          // edit skipped the in-flight guard above and would otherwise be lost.
          const latest = sessionsRef.current.find((s) => s.id === session.id);

          if (latest && !sameDraft(latest, session)) {
            await saveRef.current?.({ ...latest, noteId: created.id });

            return;
          }
        } else {
          const updated = await updateNote({
            userId: session.subjectId,
            noteId: session.noteId,
            ...payload,
          }).unwrap();

          dispatch(markSessionSaved({ id: session.id, lastSavedAt: updated.updated_at }));
        }
      } catch {
        setErrorMessage(strings.saveError);
      } finally {
        setSaving(false);
      }
    },
    [createNote, updateNote, dispatch],
  );

  saveRef.current = save;

  const runSave = useCallback(
    (sessionId: string) => {
      const session = sessionsRef.current.find((s) => s.id === sessionId);

      if (session) void save(session);
      else setSaving(false);
    },
    [save],
  );

  // An edit debounces a save. Only edits schedule one, so opening or closing a
  // note without typing never saves.
  const scheduleSave = useCallback(
    (sessionId: string) => {
      setSaving(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      pendingSaveIdRef.current = sessionId;
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        pendingSaveIdRef.current = null;
        runSave(sessionId);
      }, AUTOSAVE_DEBOUNCE_MS);
    },
    [runSave],
  );

  // Flush a pending debounced save (before minimizing, closing, or switching
  // notes). No-op when nothing is pending, so an unedited note is never
  // re-saved.
  const flushSave = useCallback(() => {
    if (!saveTimerRef.current) return;

    const sessionId = pendingSaveIdRef.current;

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    pendingSaveIdRef.current = null;
    if (sessionId) runSave(sessionId);
  }, [runSave]);

  // When the open note changes (navigation, subject switch, restoring another
  // card) flush the outgoing note's pending save so its last edit isn't
  // stranded in a timer that would fire against the newly-opened note.
  useEffect(() => {
    flushSave();
  }, [openSession?.id, flushSave]);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  const handleMinimize = useCallback(() => {
    if (!openSession) return;

    flushSave();
    dispatch(minimizeNote(openSession.id));
  }, [openSession, flushSave, dispatch]);

  const handleClose = useCallback(() => {
    if (!openSession) return;

    flushSave();
    dispatch(closeNote(openSession.id));
  }, [openSession, flushSave, dispatch]);

  // Stay mounted while an error is pending so a save that failed as the note
  // closed can still surface its snackbar.
  if (!openSession && minimizedSessions.length === 0 && !errorMessage) return null;

  const saveStatus: SaveStatus = saving ? 'saving' : openSession?.noteId != null ? 'saved' : 'idle';

  return (
    <>
      {openSession && (
        <CoachNoteEditorModal
          key={openSession.id}
          mode={openSession.mode}
          body={openSession.body}
          chips={openSession.chips}
          saveStatus={saveStatus}
          createdAt={openSession.createdAt}
          lastSavedAt={openSession.lastSavedAt}
          author={openSession.author}
          onChangeBody={(body) => {
            dispatch(setDraftBody({ id: openSession.id, body }));
            scheduleSave(openSession.id);
          }}
          onAddChip={(chip) => {
            dispatch(addChip({ id: openSession.id, chip }));
            scheduleSave(openSession.id);
          }}
          onUpdateChip={(chip) => {
            dispatch(updateChip({ id: openSession.id, chip }));
            scheduleSave(openSession.id);
          }}
          onRemoveChip={(localId) => {
            dispatch(removeChip({ id: openSession.id, localId }));
            scheduleSave(openSession.id);
          }}
          onAddRegistryEntry={(entry) => {
            dispatch(addRegistryEntry({ id: openSession.id, entry }));
            scheduleSave(openSession.id);
          }}
          onMinimize={handleMinimize}
          onClose={handleClose}
        />
      )}

      {minimizedSessions.length > 0 && (
        <Box sx={minimizedBarSx}>
          {minimizedSessions.map((session) => (
            <MinimizedNote
              key={session.id}
              sessionId={session.id}
              mode={session.mode}
              createdAt={session.createdAt}
              body={session.body}
              onRestore={() => dispatch(restoreNote(session.id))}
              onClose={() => dispatch(closeNote(session.id))}
            />
          ))}
        </Box>
      )}

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const minimizedBarSx: SxProps<Theme> = ({ backrs, zIndex }) => ({
  position: 'fixed',
  bottom: backrs.spacing['spacing-xl'],
  right: backrs.spacing['spacing-xl'],
  zIndex: zIndex.drawer + 2,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  gap: backrs.spacing['spacing-md'],
  maxWidth: `calc(100% - ${backrs.spacing['spacing-3xl']})`,
});
